import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import OnboardingStep from '../components/OnboardingStep';
import ThemeToggle from '../components/ThemeToggle';

interface OnboardingPreferences {
  primaryUseCase: string;
  teamSize: string;
  interests: string[];
  notifications: boolean;
}

const TOTAL_STEPS = 4;

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStep = Math.min(
    Math.max(parseInt(searchParams.get('step') || '1', 10), 1),
    TOTAL_STEPS
  );
  const [step, setStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preferences, setPreferences] = useState<OnboardingPreferences>({
    primaryUseCase: '',
    teamSize: '',
    interests: [],
    notifications: true,
  });

  useEffect(() => {
    setSearchParams({ step: step.toString() }, { replace: true });
  }, [step, setSearchParams]);

  const useCases = [
    { value: 'research', label: 'Market Research & Competitive Analysis' },
    { value: 'content', label: 'Content Creation & Writing' },
    { value: 'support', label: 'Customer Support & FAQs' },
    { value: 'sales', label: 'Sales Outreach & Lead Generation' },
    { value: 'operations', label: 'Business Operations & Automation' },
    { value: 'other', label: 'Something Else' },
  ];

  const teamSizes = [
    { value: 'solo', label: 'Just me' },
    { value: 'small', label: '2-10 people' },
    { value: 'medium', label: '11-50 people' },
    { value: 'large', label: '50+ people' },
  ];

  const interestOptions = [
    { value: 'agent_swarm', label: 'AI Agent Swarms' },
    { value: 'knowledge_base', label: 'Private Knowledge Base' },
    { value: 'monitoring', label: 'Website Monitoring' },
    { value: 'integrations', label: 'Third-party Integrations' },
    { value: 'analytics', label: 'Analytics & Insights' },
  ];

  const gettingStartedItems = [
    { title: 'Create your first swarm', path: '/swarm' },
    { title: 'Upload knowledge documents', path: '/knowledge' },
    { title: 'Explore agent templates', path: '/templates' },
    { title: 'Set up website monitoring', path: '/projects' },
  ];

  const handleInterestToggle = (value: string) => {
    setPreferences((prev) => {
      const interests = prev.interests.includes(value)
        ? prev.interests.filter((i) => i !== value)
        : [...prev.interests, value];
      return { ...prev, interests };
    });
  };

  const updateStoredUser = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        user.onboarding_completed = true;
        localStorage.setItem('user', JSON.stringify(user));
      }
    } catch {
      // ignore parse errors
    }
  };

  const handleComplete = async (redirectTo = '/dashboard') => {
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      await api.post('/api/v1/onboarding/complete', {
        steps_completed: [
          'welcome',
          'use_case',
          'team_preferences',
          'getting_started',
        ],
        preferences,
      });
      updateStoredUser();
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to complete onboarding';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <OnboardingStep
            title="Welcome to InsightSwarm"
            description="Let's get you set up in just a few quick steps. InsightSwarm helps small businesses harness AI agent swarms for research, content, support, and more."
            stepNumber={step}
            totalSteps={TOTAL_STEPS}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'Autonomous Agent Swarms',
                  description: 'Deploy specialized AI agents that work together on complex tasks.',
                },
                {
                  title: 'Private Knowledge Base',
                  description: 'Upload documents and URLs to ground responses in your data.',
                },
                {
                  title: 'Website Monitoring',
                  description: 'Track competitor and partner sites for changes.',
                },
                {
                  title: 'Secure & Private',
                  description: 'Your data stays in your organization with tenant isolation.',
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                >
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </OnboardingStep>
        );

      case 2:
        return (
          <OnboardingStep
            title="What brings you to InsightSwarm?"
            description="This helps us tailor your experience and recommend the right agents."
            stepNumber={step}
            totalSteps={TOTAL_STEPS}
          >
            <div className="space-y-3">
              {useCases.map((useCase) => (
                <label
                  key={useCase.value}
                  className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                    preferences.primaryUseCase === useCase.value
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="useCase"
                    value={useCase.value}
                    checked={preferences.primaryUseCase === useCase.value}
                    onChange={(e) =>
                      setPreferences((prev) => ({ ...prev, primaryUseCase: e.target.value }))
                    }
                    className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="ml-3 text-gray-900 dark:text-white">{useCase.label}</span>
                </label>
              ))}
            </div>
          </OnboardingStep>
        );

      case 3:
        return (
          <OnboardingStep
            title="Tell us about your team"
            description="We'll use this to suggest collaboration features and pricing."
            stepNumber={step}
            totalSteps={TOTAL_STEPS}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Team size
                </label>
                <select
                  value={preferences.teamSize}
                  onChange={(e) =>
                    setPreferences((prev) => ({ ...prev, teamSize: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select team size</option>
                  {teamSizes.map((size) => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Features you're interested in
                </label>
                <div className="space-y-2">
                  {interestOptions.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={preferences.interests.includes(option.value)}
                        onChange={() => handleInterestToggle(option.value)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="ml-3 text-gray-900 dark:text-white">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </OnboardingStep>
        );

      case 4:
        return (
          <OnboardingStep
            title="You're all set!"
            description="Here's what you can do next to get the most out of InsightSwarm."
            stepNumber={step}
            totalSteps={TOTAL_STEPS}
          >
            <div className="space-y-3">
              {gettingStartedItems.map((item) => (
                <button
                  key={item.title}
                  onClick={() => handleComplete(item.path)}
                  disabled={loading}
                  className="w-full text-left p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-700 transition-colors disabled:opacity-50"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{item.title}</span>
                  <span className="float-right text-purple-600 dark:text-purple-400">→</span>
                </button>
              ))}
            </div>

            <label className="flex items-center mt-4">
              <input
                type="checkbox"
                checked={preferences.notifications}
                onChange={(e) =>
                  setPreferences((prev) => ({ ...prev, notifications: e.target.checked }))
                }
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                Send me product tips and beta updates via email
              </span>
            </label>
          </OnboardingStep>
        );

      default:
        return null;
    }
  };

  const canProceed =
    step === 1 ||
    (step === 2 && preferences.primaryUseCase) ||
    (step === 3 && preferences.teamSize) ||
    step === 4;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-end mb-4">
          <ThemeToggle />
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">InsightSwarm</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            AI agent swarms for small business
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="max-w-2xl mx-auto mb-6 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded flex items-start gap-2"
          >
            <svg
              className="w-5 h-5 mt-0.5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {renderStepContent()}

        <div className="max-w-2xl mx-auto mt-8 flex justify-between">
          <button
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1 || loading}
            className="px-6 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
          >
            Back
          </button>

          {step < TOTAL_STEPS ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed || loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={() => handleComplete('/dashboard')}
              disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {loading ? 'Finishing...' : 'Get Started'}
            </button>
          )}
        </div>

        <div className="max-w-2xl mx-auto mt-4 text-center">
          <button
            onClick={() => handleComplete('/dashboard')}
            disabled={loading}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
          >
            Skip onboarding
          </button>
        </div>
      </div>
    </div>
  );
}
